import React, { useState, useEffect } from 'react'
import { api } from '@services/api'
import { BarChart3, FileText, Cpu, DollarSign, Users, RefreshCw, Database } from 'lucide-react'
import { toast } from 'react-toastify'

interface DocumentStat {
  context_id: number
  name: string
  display_name: string
  document_count: number
  onedrive_count: number
  sharepoint_count: number
  database_count: number
}

interface UserStat {
  context_id: number
  name: string
  user_count: number
}

interface LlmContextStat {
  context_id: number
  name: string
  display_name: string
  calls: number
  input_tokens: number
  output_tokens: number
}

interface IntegrationStats {
  documents: DocumentStat[]
  users: UserStat[]
  llm: {
    allTime: {
      total_calls: number
      total_input_tokens: number
      total_output_tokens: number
      total_tokens: number
    }
    last30Days: {
      calls: number
      input_tokens: number
      output_tokens: number
      total_tokens: number
    }
    byContext: LlmContextStat[]
    estimatedCostUsd: number
  }
}

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

const IntegrationAdmin: React.FC = () => {
  const [stats, setStats] = useState<IntegrationStats | null>(null)
  const [loading, setLoading] = useState(true)

  const loadStats = async () => {
    setLoading(true)
    try {
      const data = await api.getIntegrationStats()
      setStats(data)
    } catch {
      toast.error('Failed to load integration stats')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStats()
  }, [])

  if (loading && !stats) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-10 w-10 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">Loading…</p>
      </div>
    )
  }

  if (!stats) return null

  const totalDocs = stats.documents.reduce((s, d) => s + d.document_count, 0)
  const totalUsers = stats.users.reduce((s, u) => s + u.user_count, 0)

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/40">
            <BarChart3 className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Integration Admin</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Cross-context usage stats, document digestion, and LLM consumption
            </p>
          </div>
        </div>
        <button
          onClick={loadStats}
          disabled={loading}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card flex items-start gap-4 p-4">
          <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/40">
            <FileText className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Documents Digested</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(totalDocs)}</p>
          </div>
        </div>

        <div className="card flex items-start gap-4 p-4">
          <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/40">
            <Cpu className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">LLM Calls (All Time)</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatNumber(stats.llm.allTime.total_calls)}
            </p>
          </div>
        </div>

        <div className="card flex items-start gap-4 p-4">
          <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/40">
            <BarChart3 className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Tokens Used (All Time)</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatNumber(stats.llm.allTime.total_tokens)}
            </p>
          </div>
        </div>

        <div className="card flex items-start gap-4 p-4">
          <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40">
            <DollarSign className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Estimated Cost (All Time)</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              ${stats.llm.estimatedCostUsd.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Context Breakdown */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Documents per Context */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-3 border-b bg-gray-50 px-5 py-4 dark:bg-white/5">
            <Database className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Documents per Context</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Context
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Total
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  OneDrive
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  SharePoint
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  DB
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {stats.documents.map((d) => (
                <tr key={d.context_id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{d.display_name}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{d.document_count}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">{d.onedrive_count}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">
                    {d.sharepoint_count}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">{d.database_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* LLM Usage per Context */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-3 border-b bg-gray-50 px-5 py-4 dark:bg-white/5">
            <Cpu className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">LLM Usage per Context</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Context
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Calls
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Input
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Output
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {stats.llm.byContext.map((l) => (
                <tr key={l.context_id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{l.display_name}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{l.calls}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">
                    {formatNumber(l.input_tokens)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">
                    {formatNumber(l.output_tokens)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Users per Context + Last 30 Days */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Users per Context */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-3 border-b bg-gray-50 px-5 py-4 dark:bg-white/5">
            <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Users per Context</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Context
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                  Members
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {stats.users.map((u) => (
                <tr key={u.context_id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{u.name}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{u.user_count}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold dark:bg-white/5">
                <td className="px-4 py-3 text-sm">Total</td>
                <td className="px-4 py-3 text-right text-sm">{totalUsers}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Last 30 Days */}
        <div className="card p-0">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-white/5">
            <BarChart3 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Last 30 Days</h2>
          </div>
          <div className="space-y-4 p-5">
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4 dark:bg-white/5">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">LLM Calls</span>
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {formatNumber(stats.llm.last30Days.calls)}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4 dark:bg-white/5">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Tokens Used</span>
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {formatNumber(stats.llm.last30Days.total_tokens)}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4 dark:bg-white/5">
              <div className="flex items-center gap-2">
                <Cpu className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Input / Output</span>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formatNumber(stats.llm.last30Days.input_tokens)} / {formatNumber(stats.llm.last30Days.output_tokens)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default IntegrationAdmin
